import { computed, inject, Injectable, signal, Signal } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { PaginatedEntityFacadeService } from "../../../../shared/store/pagination/paginated-entity-facade.service";
import {
  Untrack,
  UpdateStatus,
} from "../../../../shared/store/utils/store-utils.service";
import { NonConformiteApiService } from "./non-conformite-api.service";
import { NonConformiteStoreService } from "./non-conformite-store.service";
import {
  CloseNonConformitePayload,
  CreateNonConformitePayload,
  NcStats,
  NonConformite,
  UpdateNonConformitePayload,
} from "../../models/non-conformite.model";

@Injectable({ providedIn: "root" })
export class NonConformiteFacadeService extends PaginatedEntityFacadeService<
  NonConformite,
  NonConformiteApiService
> {
  private ncStore = inject(NonConformiteStoreService);

  selectedNcId: Signal<string | null>;
  selectedNc: Signal<NonConformite | null>;
  stats = signal<NcStats>({
    ouvertes: 0,
    critiquesOuvertes: 0,
    coutMois: 0,
    totalMois: 0,
    tauxEchappees: null,
    origineRepartition: { interne: 0, externe: 0, mixte: 0, nonRenseigne: 0 },
  });

  constructor() {
    const api = inject(NonConformiteApiService);
    const store = inject(NonConformiteStoreService);
    super(api, store as any);
    this.store.initStatus(NonConformiteFacadeService);

    const state = this.ncStore.getWritableState();

    this.selectedNcId = computed(() => state.selectedNcId());
    this.selectedNc = computed(() => {
      const id = state.selectedNcId();
      if (!id) return null;
      const entity = this.getById(id);
      return entity ? entity() : null;
    });
  }

  selectNc(id: string | null) {
    this.ncStore.patchState({ selectedNcId: id });
  }

  clearSelection() {
    this.ncStore.patchState({ selectedNcId: null });
  }

  upsertInStore(nc: NonConformite) {
    this.store.upsertOne(nc);
  }

  @Untrack
  @UpdateStatus
  async createNc(payload: CreateNonConformitePayload): Promise<NonConformite> {
    const created = await firstValueFrom(this.api.create(payload));
    this.store.upsertOne(created);
    return created;
  }

  @Untrack
  @UpdateStatus
  async patchNc(
    id: string,
    payload: UpdateNonConformitePayload,
  ): Promise<NonConformite> {
    const updated = await firstValueFrom(this.api.patch(id, payload));
    this.store.upsertOne(updated);
    return updated;
  }

  @Untrack
  @UpdateStatus
  async closeNc(
    id: string,
    payload: CloseNonConformitePayload,
  ): Promise<NonConformite> {
    const closed = await firstValueFrom(this.api.close(id, payload));
    this.store.upsertOne(closed);
    return closed;
  }

  @Untrack
  @UpdateStatus
  async cancelNc(id: string, comment?: string): Promise<NonConformite> {
    const cancelled = await firstValueFrom(this.api.cancel(id, comment));
    this.store.upsertOne(cancelled);
    return cancelled;
  }

  @Untrack
  @UpdateStatus
  async loadStats(start?: string, end?: string): Promise<NcStats> {
    const stats = await firstValueFrom(this.api.stats(start, end));
    this.stats.set(stats);
    return stats;
  }

  @Untrack
  @UpdateStatus
  async loadByLot(lotId: string): Promise<NonConformite[]> {
    const list = await firstValueFrom(this.api.loadByLot(lotId));
    list.forEach((nc) => this.store.upsertOne(nc));
    return list;
  }

  @Untrack
  @UpdateStatus
  async loadByPalette(paletteId: string): Promise<NonConformite[]> {
    const list = await firstValueFrom(this.api.loadByPalette(paletteId));
    list.forEach((nc) => this.store.upsertOne(nc));
    return list;
  }

  @Untrack
  @UpdateStatus
  async loadByChargement(chargementId: string): Promise<NonConformite[]> {
    const list = await firstValueFrom(this.api.loadByChargement(chargementId));
    list.forEach((nc) => this.store.upsertOne(nc));
    return list;
  }

  @Untrack
  @UpdateStatus
  async loadByTiers(tiersId: string): Promise<NonConformite[]> {
    const list = await firstValueFrom(this.api.loadByTiers(tiersId));
    list.forEach((nc) => this.store.upsertOne(nc));
    return list;
  }

  @Untrack
  @UpdateStatus
  async loadByArticle(articleId: string): Promise<NonConformite[]> {
    const list = await firstValueFrom(this.api.loadByArticle(articleId));
    list?.forEach((nc) => this.store.upsertOne(nc));
    return list ?? [];
  }
}
